import { meResponseSchema, returnPathSchema, type MeResponse } from '@koderek/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { t } from '../../texts/pl';

export const ME_KEY = ['me'] as const;

/** Zalogowany użytkownik albo null (401). */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await api('GET', '/auth/me', { schema: meResponseSchema });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Po zalogowaniu / aktywacji / ustawieniu hasła odpowiedź API to `me` – zapisujemy ją od razu. */
export function useSetMe() {
  const client = useQueryClient();
  return (me: MeResponse | null) => client.setQueryData(ME_KEY, me);
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api('POST', '/auth/logout'),
    onSettled: () => {
      client.setQueryData(ME_KEY, null);
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
  });
}

/** Komunikat dla użytkownika z błędu API (NFR-U5). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Błąd formatu pliku importu – serwer mówi dokładnie, czego brakuje (kolumny, wiersze).
    const message = (error.body as { message?: unknown } | null)?.message;
    if (error.code === 'import_invalid' && typeof message === 'string' && message) return message;
    return (error.code && t.errors[error.code]) || t.errors.internal_error;
  }
  return t.errors.network;
}

/** Bezpieczny adres powrotu po logowaniu – tylko ścieżki naszej aplikacji. */
export function safeReturnTo(value: string | null): string {
  return value && returnPathSchema.safeParse(value).success ? value : '/';
}
